import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AnnouncementBannerComponent } from './announcement-banner.component';
import { HeaderComponent } from './header.component';
import { SidebarComponent } from './sidebar.component';
import { ShellStore } from './shell.store';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterOutlet, AnnouncementBannerComponent, HeaderComponent, SidebarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'cerrarMenuMovil()',
  },
  template: `
    <a class="skip-link" href="#contenido-principal" data-testid="shell-skip-link" (click)="saltarAlContenido($event)">Saltar al contenido</a>
    <app-announcement-banner />
    <div
      class="shell"
      [class.shell--sidebar-collapsed]="store.sidebarCollapsed()"
      [class.shell--sidebar-open]="store.isMobileViewport() && !store.sidebarCollapsed()"
    >
      <app-header />
      @if (store.isMobileViewport() && !store.sidebarCollapsed()) {
        <button class="shell-backdrop" type="button" tabindex="-1" aria-label="Cerrar menú lateral" (click)="store.closeSidebar()"></button>
      }
      <div
        class="shell-sidebar-region"
        data-testid="shell-sidebar-region"
        [attr.inert]="store.isMobileViewport() && store.sidebarCollapsed() ? '' : null"
        #sidebarRegion
      >
        <app-sidebar
          data-testid="shell-sidebar"
          [collapsed]="store.sidebarCollapsed()"
          [sections]="store.sections()"
        ></app-sidebar>
      </div>
      <main class="shell-content" id="contenido-principal" tabindex="-1" data-testid="shell-content" #contenido>
        <router-outlet />
      </main>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        min-height: 100dvh;
        --shell-header-z-index: 100;
        --shell-sidebar-mobile-z-index: 130;
        --shell-backdrop-z-index: 120;
      }

      .shell {
        display: grid;
        grid-template-rows: var(--header-h) minmax(0, 1fr);
        grid-template-columns: var(--sidebar-w) minmax(0, 1fr);
        grid-template-areas:
          'header  header'
          'sidebar content';
        min-height: 100dvh;
        background: var(--bg);
        align-items: start;
      }

      .shell--sidebar-collapsed {
        grid-template-columns: var(--sidebar-w-collapsed) 1fr;
      }

      .shell-sidebar-region {
        grid-area: sidebar;
        min-width: 0;
        min-height: 0;
        position: sticky;
        top: var(--header-h);
        height: calc(100dvh - var(--header-h));
        align-self: start;
        overflow-y: auto;
        overflow-x: hidden;
        background: var(--surface);
        border-right: 1px solid var(--border);
      }

      .shell-content {
        grid-area: content;
        padding: var(--esp-contenedor) var(--esp-contenedor) var(--shell-content-padding-bottom);
        color: var(--text);
        min-height: 0;
        min-width: 0;
      }

      .shell-content:focus {
        outline: none;
      }

      .skip-link {
        position: absolute;
        left: -9999px;
        top: 0;
        z-index: 200;
      }

      .skip-link:focus {
        left: var(--esp-contenedor);
        top: 8px;
        padding: 8px 16px;
        border-radius: var(--radius-pill);
        background: var(--primary);
        color: var(--surface);
        font-weight: 500;
        text-decoration: none;
      }

      @media (max-width: 1024px) {
        .shell {
          height: auto;
          min-height: 100dvh;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          overflow-y: visible;
          grid-template-columns: 1fr;
          grid-template-areas:
            'header'
            'content';
        }

        .shell-sidebar-region {
          position: fixed;
          inset: var(--header-h) auto 0 0;
          width: var(--sidebar-w);
          z-index: var(--shell-sidebar-mobile-z-index);
          transform: translateX(-100%);
          visibility: hidden;
          transition: transform 180ms ease, visibility 0s linear 180ms;
          box-shadow: var(--shadow-drawer);
        }

        .shell--sidebar-open .shell-sidebar-region {
          transform: translateX(0);
          visibility: visible;
          transition-delay: 0s;
        }

        .shell-content {
          height: auto;
          min-height: calc(100dvh - var(--header-h));
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          overflow-y: visible;
        }

        .shell-backdrop {
          position: fixed;
          inset: var(--header-h) 0 0;
          z-index: var(--shell-backdrop-z-index);
          border: none;
          background: var(--overlay);
          backdrop-filter: blur(2px);
        }
      }

      @media (max-width: 768px) {
        .shell-content {
          padding: var(--esp-contenedor) var(--esp-contenedor) var(--shell-content-padding-bottom);
        }
      }
    `,
  ],
})
export class AppShellComponent {
  protected readonly store = inject(ShellStore);
  private readonly contenido = viewChild<ElementRef<HTMLElement>>('contenido');
  private readonly sidebarRegion = viewChild<ElementRef<HTMLElement>>('sidebarRegion');

  constructor() {
    effect(() => {
      const abierto = this.store.isMobileViewport() && !this.store.sidebarCollapsed();
      if (abierto) {
        setTimeout(() => this.sidebarRegion()?.nativeElement.querySelector<HTMLElement>('.nav-item')?.focus(), 0);
      }
    });
  }

  protected saltarAlContenido(event: Event): void {
    event.preventDefault();
    this.contenido()?.nativeElement.focus();
  }

  protected cerrarMenuMovil(): void {
    if (!this.store.isMobileViewport() || this.store.sidebarCollapsed()) {
      return;
    }

    if (document.querySelector('.identity-popover__panel')) {
      return;
    }

    this.store.closeSidebar();
    document.querySelector<HTMLElement>('[data-testid="shell-menu-toggle"]')?.focus();
  }
}
